import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Users, Zap, Sparkles, User as UserIcon, ArrowRight, Layers, KeyRound, Copy, X, Wifi, WifiOff, RefreshCw } from "lucide-react";
import SiteChrome from "@/components/SiteChrome";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useVsHumanSocket } from "@/lib/vsHumanSocket";
import { AvatarSVG, DEFAULT_AVATAR } from "@/lib/avatarBuilder";
import AvatarBuilder from "@/components/AvatarBuilder";
import PlayerProfileCard from "@/components/PlayerProfileCard";
import { useI18n } from "@/lib/i18n";

export default function VsHumano() {
  const { t, lang } = useI18n();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  // Rematch intent, coming from the end-of-duel screen. Kept in refs so the
  // invite fires exactly once even across re-renders of the WS handler.
  const rematchToRef = useRef(searchParams.get("rematch_to") || "");
  const rematchNameRef = useRef(searchParams.get("rematch_name") || "");
  const rematchGameIdRef = useRef(searchParams.get("rematch_game_id") || "");
  // When arriving from GamePlay's "Desforra" button, the invite has ALREADY
  // been sent via the still-open WebSocket. Do not re-fire it from the lobby
  // auto-invite effect below — we just want to sit on this page waiting for
  // `match_start` (the opponent's accept flows through the App-level global
  // listener while they're still on their end-of-duel result screen).
  const rematchAlreadySentRef = useRef(searchParams.get("rematch_pending") === "1");
  const rematchFiredRef = useRef(rematchAlreadySentRef.current);
  const [rematchWaiting, setRematchWaiting] = useState(!!rematchToRef.current);
  // 10-second local countdown for the "A aguardar…" pill. Starts at 10 as
  // soon as the rematch invite has left the GamePlay screen (i.e. the URL
  // carries `rematch_pending=1`). When it hits 0 we show a toast and clear
  // the waiting state — matches the backend's 30s invite TTL loose upper
  // bound, but is deliberately shorter to give the winner instant feedback.
  const [rematchCountdown, setRematchCountdown] = useState(
    rematchToRef.current ? 10 : 0,
  );
  const [games, setGames] = useState([]);
  const [name, setName] = useState(() => sessionStorage.getItem("player_name") || "");
  const [school, setSchool] = useState(() => sessionStorage.getItem("player_school") || "");
  const [nationality, setNationality] = useState(() => sessionStorage.getItem("player_nationality") || "PT");
  const [editingProfile, setEditingProfile] = useState(() => !sessionStorage.getItem("player_name"));
  const [selectedGame, setSelectedGame] = useState(null);
  const [selectedLevel, setSelectedLevel] = useState(1);
  const [selectedTierId, setSelectedTierId] = useState("");
  const [lobby, setLobby] = useState([]); // [{user_id,name,avatar_cfg}]
  const [invite, setInvite] = useState(null); // incoming invite
  const [pendingInviteTo, setPendingInviteTo] = useState(null); // outgoing invite target user_id
  const [pendingInviteTimer, setPendingInviteTimer] = useState(0); // seconds remaining for outgoing invite (10→0)
  const [inviteTimer, setInviteTimer] = useState(0); // seconds remaining for incoming invite (10→0)
  const [privateCode, setPrivateCode] = useState("");
  const [enterCode, setEnterCode] = useState("");
  const [showPrivate, setShowPrivate] = useState(false);
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);
  // Read the latest player avatar from sessionStorage on every mount AND every
  // time the window regains focus (so updates done elsewhere — TrainStart, etc.
  // — propagate immediately into the lobby roster).
  const [avatarCfg, setAvatarCfg] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem("player_avatar_cfg") || "null") || DEFAULT_AVATAR; }
    catch { return DEFAULT_AVATAR; }
  });
  useEffect(() => {
    const reload = () => {
      try {
        const next = JSON.parse(sessionStorage.getItem("player_avatar_cfg") || "null") || DEFAULT_AVATAR;
        setAvatarCfg(next);
      } catch { /* ignore */ }
    };
    window.addEventListener("storage", reload);
    window.addEventListener("focus", reload);
    return () => {
      window.removeEventListener("storage", reload);
      window.removeEventListener("focus", reload);
    };
  }, []);

  // Pre-rasterization of gameplay assets is NOT done on this lobby screen —
  // it competes with input handling. The GamePlay screen has its own warmup
  // that runs after the "READY" modal appears (see `GamePlay.jsx`).

  // Load games once — filtered per-language (a game marked inactive in EN/ES
  // stays hidden from that idiom, same as VS TEMPO / TrainStart).
  useEffect(() => {
    api.get(`/games?active_only=true&status=training&lang=${encodeURIComponent(lang || "pt")}`).then((r) => {
      const list = (r.data || []);
      setGames(list);
      if (rematchGameIdRef.current) {
        const g = list.find((x) => x.id === rematchGameIdRef.current);
        if (g) setSelectedGame(g);
      }
    }).catch(() => {});
  }, [lang]);

  // Adapt level when game changes
  useEffect(() => {
    if (!selectedGame) return;
    if (selectedGame.level_system === "tiers" && Array.isArray(selectedGame.tiers) && selectedGame.tiers.length > 0) {
      const first = selectedGame.tiers[0];
      setSelectedTierId(first.id);
      setSelectedLevel(parseInt(first.level, 10) || 1);
    } else {
      setSelectedTierId("");
      setSelectedLevel(1);
    }
  }, [selectedGame]);

  const handleMessage = (msg) => {
    if (msg.type === "lobby") setLobby(msg.users || []);
    else if (msg.type === "invite_received") {
      // Rematch invites are owned by the global App-level dialog
      // (components/GlobalVsHumanRematch.jsx). Ignore them here so the local
      // countdown/state doesn't fight with the global one.
      if (msg.rematch) return;
      setInviteTimer(10);
      setInvite(msg);
    }
    else if (msg.type === "invite_cancelled") {
      setInvite((curr) => (curr?.invite_id === msg.invite_id ? null : curr));
      setInviteTimer(0);
    }
    else if (msg.type === "invite_response") {
      setPendingInviteTo(null);
      setPendingInviteTimer(0);
      if (!msg.accepted) toast.error(t("vsh_invite_declined"));
    }
    else if (msg.type === "private_code") { setPrivateCode(msg.code); toast.success(t("vsh_private_code_ready", { code: msg.code })); }
    else if (msg.type === "match_start") {
      // Persist what GamePlay needs
      sessionStorage.setItem("player_name", name.trim());
      sessionStorage.setItem("player_school", (school || "").trim());
      sessionStorage.setItem("player_nationality", nationality || "PT");
      // CRITICAL: adopt the host's level from `match_start` (backend forces
      // both peers to share the host's game+level). Without this, a guest
      // who picked a different level in the lobby ends up with a stale
      // `player_level` in sessionStorage → the roulette hook uses the
      // wrong `activePosition` slot → the two peers diverge from round-N.
      const matchLevel = Number(msg.level || selectedLevel) || 1;
      sessionStorage.setItem("player_level", String(matchLevel));
      // Propagate the HOST's tier_id (arrives on `match_start`) so both
      // peers show the SAME "escalão" label at the game screen. Falls
      // back to the guest's own selection when the host never picked a
      // tier (defensive — the host normally always has one).
      const matchTierId = msg.tier_id || selectedTierId || "";
      if (matchTierId) sessionStorage.setItem("player_tier_id", matchTierId);
      else sessionStorage.removeItem("player_tier_id");
      sessionStorage.setItem("vsh_session", JSON.stringify({
        session_id: msg.session_id,
        game: msg.game,
        questions: msg.questions,
        sequence: msg.sequence || [],
        cards: msg.cards || [],
        rounds: msg.rounds || [],
        supert_letters: msg.supert_letters,
        opponent: msg.opponent,
        // Opponent's own escalão/nível (each peer plays their OWN since
        // 2026-02). Kept next to `opponent` so GamePlay can render the
        // right label on the opponent's pill.
        opponent_level: msg.opponent_level ?? null,
        opponent_tier_id: msg.opponent_tier_id || null,
      }));
      // Include the session_id as a query param so React Router treats a
      // rematch as a fresh route (forces GamePlay to remount and read the
      // new `vsh_session` from sessionStorage).
      nav(`/jogar/${msg.game.id}?mode=vs_human&vsh=${msg.session_id}`);
    }
    else if (msg.type === "error") {
      // Backend rejected a message — most commonly "Utilizador indisponível"
      // if the rematch invite hit a peer who already disconnected. Cancel
      // the waiting pill and tell the winner what happened.
      if (rematchWaiting) {
        setRematchWaiting(false);
        setRematchCountdown(0);
        rematchFiredRef.current = true;
        toast.error(
          t("vsh_rematch_offline", { name: rematchNameRef.current || "" }),
        );
        // Clear the URL flags so a refresh doesn't retry.
        const next = new URLSearchParams(searchParams);
        ["rematch_to", "rematch_name", "rematch_game_id", "rematch_pending"].forEach((k) => next.delete(k));
        setSearchParams(next, { replace: true });
      } else {
        toast.error(msg.message || "Erro");
      }
    }
    else if (msg.type === "invite_response" && rematchWaiting && msg.accepted === false) {
      // Opponent explicitly declined the rematch.
      setRematchWaiting(false);
      setRematchCountdown(0);
      rematchFiredRef.current = true;
      toast.error(
        t("vsh_rematch_declined", { name: rematchNameRef.current || "" }),
      );
      const next = new URLSearchParams(searchParams);
      ["rematch_to", "rematch_name", "rematch_game_id", "rematch_pending"].forEach((k) => next.delete(k));
      setSearchParams(next, { replace: true });
    }
  };

  const { connected, send } = useVsHumanSocket(handleMessage);

  // Re-send hello whenever (name, game, level) changes & connected
  useEffect(() => {
    if (!connected) return;
    if (!name.trim() || !selectedGame) return;
    send({
      type: "hello",
      name: name.trim(),
      game_id: selectedGame.id,
      level: selectedLevel,
      tier_id: selectedTierId || null,
      lang,
      avatar_cfg: avatarCfg,
    });
  }, [connected, name, selectedGame, selectedLevel, selectedTierId, lang, avatarCfg, send]);

  const invitePeer = (peer, opts = {}) => {
    if (!name.trim()) { toast.error(t("vsh_name_required")); return; }
    // Order matters: timer FIRST, then target. The countdown useEffect's
    // dependency order means React renders with timer=10 already set when
    // pendingInviteTo flips to non-null, so the "<=0 means expired" branch
    // never sees the brief initial state where timer=0 + target=peer.user_id.
    setPendingInviteTimer(10);
    setPendingInviteTo(peer.user_id);
    const payload = { type: "invite", to: peer.user_id };
    if (opts.rematch) payload.rematch = true;
    send(payload);
    toast.success(t("vsh_invite_sent", { name: peer.name }));
  };

  // ---- Rematch: auto-fire the invite once the opponent shows up in the
  // lobby. Requires that WE also completed `hello` (which needs name + game).
  // If the opponent doesn't return to the lobby within 15s, we drop the
  // intent and just leave the user in the normal lobby view.
  useEffect(() => {
    if (!rematchToRef.current || rematchFiredRef.current) return undefined;
    if (!connected || !name.trim() || !selectedGame) return undefined;
    const target = lobby.find((u) => u.user_id === rematchToRef.current);
    if (target) {
      rematchFiredRef.current = true;
      setRematchWaiting(false);
      // Clear the URL params so a refresh doesn't retry.
      const next = new URLSearchParams(searchParams);
      ["rematch_to", "rematch_name", "rematch_game_id"].forEach((k) => next.delete(k));
      setSearchParams(next, { replace: true });
      invitePeer(target, { rematch: true });
    }
    // Also expire the waiting state after 15 seconds if the opponent never returns.
    if (!target) {
      const tid = setTimeout(() => {
        if (rematchFiredRef.current) return;
        rematchFiredRef.current = true;
        setRematchWaiting(false);
        const next = new URLSearchParams(searchParams);
        ["rematch_to", "rematch_name", "rematch_game_id"].forEach((k) => next.delete(k));
        setSearchParams(next, { replace: true });
        toast.info(t("vsh_rematch_unavailable", { name: rematchNameRef.current || "" }));
      }, 15000);
      return () => clearTimeout(tid);
    }
    return undefined;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connected, name, selectedGame, lobby]);

  const respondInvite = (accept) => {
    if (!invite) return;
    send({ type: "invite_response", invite_id: invite.invite_id, accept });
    setInvite(null);
    setInviteTimer(0);
  };

  // 10-second countdown for the REMATCH waiting pill — the pill shows "A
  // aguardar {name}…" plus a colored `Ns` badge. When the counter hits 0
  // and we're still waiting, tell the winner the opponent didn't reply and
  // clear all rematch state so they can invite someone else. The backend
  // invite TTL is 30s (server-side), we use 10s locally for snappy UX.
  useEffect(() => {
    if (!rematchWaiting || rematchCountdown <= 0) return undefined;
    if (rematchCountdown === 1) {
      const tid = setTimeout(() => {
        setRematchCountdown(0);
        setRematchWaiting(false);
        rematchFiredRef.current = true;
        toast.error(t("vsh_rematch_offline", { name: rematchNameRef.current || "" }));
        const next = new URLSearchParams(searchParams);
        ["rematch_to", "rematch_name", "rematch_game_id", "rematch_pending"].forEach((k) => next.delete(k));
        setSearchParams(next, { replace: true });
      }, 1000);
      return () => clearTimeout(tid);
    }
    const tid = setTimeout(() => setRematchCountdown((s) => s - 1), 1000);
    return () => clearTimeout(tid);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rematchWaiting, rematchCountdown]);

  // 10-second countdown for the OUTGOING invite (inviter side). When the
  // counter ticks down to 1→0, expire the invite. While the timer is 0 we
  // simply bail (no-op) — this avoids spuriously clearing the pending state
  // during the brief render where pendingInviteTo just became truthy but
  // setPendingInviteTimer(10) hasn't flushed yet (defensive).
  useEffect(() => {
    if (!pendingInviteTo || pendingInviteTimer <= 0) return undefined;
    if (pendingInviteTimer === 1) {
      const tid = setTimeout(() => {
        setPendingInviteTimer(0);
        setPendingInviteTo(null);
        toast.error(t("vsh_invite_expired"));
      }, 1000);
      return () => clearTimeout(tid);
    }
    const tid = setTimeout(() => setPendingInviteTimer((s) => s - 1), 1000);
    return () => clearTimeout(tid);
  }, [pendingInviteTo, pendingInviteTimer, t]);

  // 10-second countdown for the INCOMING invite (invitee side). Mirror logic:
  // bail while timer is 0 (pre-init), and only auto-reject on the 1→0 tick.
  useEffect(() => {
    if (!invite || inviteTimer <= 0) return undefined;
    if (inviteTimer === 1) {
      const tid = setTimeout(() => {
        send({ type: "invite_response", invite_id: invite.invite_id, accept: false });
        setInvite(null);
        setInviteTimer(0);
        toast.info(t("vsh_invite_timeout_auto_decline"));
      }, 1000);
      return () => clearTimeout(tid);
    }
    const tid = setTimeout(() => setInviteTimer((s) => s - 1), 1000);
    return () => clearTimeout(tid);
  }, [invite, inviteTimer, send, t]);

  // (Note: inviteTimer is initialized to 10 in the `invite_received` message
  // handler BEFORE setInvite is called, ensuring the countdown effect sees
  // a fresh value as soon as the dialog renders.)

  const createPrivate = () => {
    const missing = [];
    if (!name.trim()) missing.push(t("vsh_field_name"));
    if (!selectedGame) missing.push(t("vsh_field_game"));
    if (missing.length > 0) {
      toast.error(
        missing.length === 1
          ? t("vsh_fill_field_before_room", { field: missing[0] })
          : t("vsh_fill_missing_fields", { list: missing.join(" + ") }),
      );
      return;
    }
    send({ type: "private_create" });
  };
  const joinPrivate = () => {
    const c = enterCode.trim();
    const missing = [];
    if (!name.trim()) missing.push(t("vsh_field_name"));
    if (!/^\d{4}$/.test(c)) missing.push(t("vsh_field_code"));
    if (missing.length > 0) {
      toast.error(
        missing.length === 1
          ? (missing[0] === t("vsh_field_name") ? t("vsh_name_required") : t("vsh_pick_code"))
          : t("vsh_fill_missing_fields", { list: missing.join(" + ") }),
      );
      return;
    }
    // For private join, game/level adopted from host if not set, but require name + send hello first
    send({
      type: "hello",
      name: name.trim(),
      game_id: selectedGame?.id || "",
      level: selectedLevel,
      tier_id: selectedTierId || null,
      lang,
      avatar_cfg: avatarCfg,
    });
    setTimeout(() => send({ type: "private_join", code: c }), 80);
  };

  return (
    <SiteChrome>
      <section className="flex-1 max-w-3xl w-full mx-auto px-4 py-8">
        <div className="flex items-center gap-3 mb-2">
          <h1 className="text-3xl md:text-4xl font-[Fredoka] font-extrabold text-violet-700 tracking-tight" data-testid="vsh-title">{t("mode_vs_human")}</h1>
          <span className={`text-xs px-2 py-1 rounded-full font-bold flex items-center gap-1 ${connected ? "bg-green-100 text-green-700" : "bg-rose-100 text-rose-700"}`}>
            {connected ? <><Wifi className="w-3 h-3"/> online</> : <><WifiOff className="w-3 h-3"/> {t("vsh_connecting")}</>}
          </span>
        </div>
        <p className="text-brand-muted mb-6 text-sm">{t("vsh_page_intro")}</p>

        {rematchWaiting && rematchNameRef.current && (
          <div
            className="mb-4 rounded-xl border-2 border-amber-300 bg-amber-50 px-4 py-2.5 flex items-center gap-3 text-sm font-semibold text-amber-800"
            data-testid="vsh-rematch-waiting"
          >
            <RefreshCw className="w-4 h-4 animate-spin shrink-0" />
            <span className="flex-1">
              {t("vsh_rematch_waiting", { name: rematchNameRef.current })}
            </span>
            <span
              className={`inline-flex items-center justify-center min-w-[28px] h-7 px-2 rounded-full text-xs font-extrabold tabular-nums shadow-sm ${
                rematchCountdown <= 3 ? "bg-rose-500 text-white" : "bg-amber-500 text-white"
              }`}
              data-testid="vsh-rematch-countdown-pill"
              title={t("vsh_invite_expire_tip")}
            >
              {rematchCountdown}s
            </span>
          </div>
        )}

        <Step number={1} title="">
          <PlayerProfileCard
            title={t("mode_vs_human")}
            editing={editingProfile}
            setEditing={setEditingProfile}
            name={name} setName={setName}
            school={school} setSchool={setSchool}
            nationality={nationality} setNationality={setNationality}
            avatarCfg={avatarCfg}
            onPickAvatar={() => setShowAvatarPicker(true)}
            testIdPrefix="vsh-profile"
          />
        </Step>

        <Step number={2} title={t("vsh_step_choose_game")}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {games.map((g) => (
              <button
                key={g.id}
                data-testid={`vsh-game-${g.id}`}
                onClick={() => setSelectedGame(g)}
                className={`text-left rounded-2xl border-2 p-3 transition shadow-sm ${selectedGame?.id === g.id ? "bg-violet-50 border-violet-400 ring-2 ring-violet-300" : "bg-brand-bg2 border-brand-muted/30 hover:border-violet-300"}`}
              >
                <div className="font-[Fredoka] font-bold text-brand-text">{g.name}</div>
                <div className="text-xs text-brand-muted mt-1 line-clamp-1">{g.description || ""}</div>
              </button>
            ))}
          </div>
        </Step>

        <Step number={3} title={t("vsh_step_choose_level")} hint={!selectedGame ? t("vsh_hint_pick_game_first") : null}>
          {selectedGame && (
            <div className="max-w-md">
              <Label className="flex items-center gap-2 mb-1.5"><Layers className="w-4 h-4 text-brand-muted"/>
                {selectedGame.level_system === "tiers" ? t("vsh_level_or_tier") : t("vsh_level_label")}
              </Label>
              {selectedGame.level_system === "tiers" && Array.isArray(selectedGame.tiers) && selectedGame.tiers.length > 0 ? (
                <Select value={selectedTierId} onValueChange={(id) => { const tr = selectedGame.tiers.find(x=>x.id===id); if(tr){setSelectedTierId(id); setSelectedLevel(parseInt(tr.level,10)||1);} }}>
                  <SelectTrigger data-testid="vsh-level"><SelectValue/></SelectTrigger>
                  <SelectContent>{selectedGame.tiers.map((tr)=><SelectItem key={tr.id} value={tr.id}>{tr.name} <span className="text-brand-muted text-xs">({t("vsh_level_label")} {tr.level})</span></SelectItem>)}</SelectContent>
                </Select>
              ) : (
                <Select value={String(selectedLevel)} onValueChange={(v) => setSelectedLevel(parseInt(v, 10))}>
                  <SelectTrigger data-testid="vsh-level"><SelectValue/></SelectTrigger>
                  <SelectContent>{(selectedGame.format === "quiz" ? [1,2,3,4] : [1,2,3,4,5]).map((n)=><SelectItem key={n} value={String(n)} data-testid={`vsh-level-${n}`}>{t("vsh_level_label")} {n}</SelectItem>)}</SelectContent>
                </Select>
              )}
            </div>
          )}
        </Step>

        <Step number={4} title={t("vsh_step_who_online")} hint={!selectedGame ? t("vsh_hint_pick_game_first") : !name.trim() ? t("vsh_hint_pick_name") : null}>
          {selectedGame && name.trim() && (
            lobby.length === 0 ? (
              <div className="rounded-2xl border-2 border-dashed border-violet-200 p-6 text-center text-brand-muted bg-brand-bg2/60" data-testid="vsh-empty-lobby">
                <motion.div
                  animate={{ scale: [1, 1.15, 1], opacity: [0.6, 1, 0.6] }}
                  transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
                >
                  <Users className="w-10 h-10 mx-auto mb-2 text-violet-400"/>
                </motion.div>
                <div className="font-bold text-violet-700">{t("vsh_searching")}</div>
                <div className="text-xs mt-1" dangerouslySetInnerHTML={{ __html: t("vsh_searching_sub_html") }} />
                <div className="flex justify-center gap-1 mt-3">
                  {[0, 1, 2].map((i) => (
                    <motion.span
                      key={i}
                      className="w-2 h-2 rounded-full bg-violet-500"
                      animate={{ y: [0, -6, 0], opacity: [0.4, 1, 0.4] }}
                      transition={{ duration: 0.9, repeat: Infinity, delay: i * 0.18 }}
                    />
                  ))}
                </div>
              </div>
            ) : (
              <ul className="space-y-2" data-testid="vsh-lobby-list">
                {lobby.map((p) => (
                  <li key={p.user_id} className="flex items-center gap-3 bg-brand-bg2 rounded-2xl border-2 border-brand-muted/30 p-2.5 shadow-sm" data-testid={`vsh-peer-${p.user_id}`}>
                    <div className="w-10 h-10 rounded-full overflow-hidden bg-brand-bg2 border-2 border-brand-bg2 shrink-0 grid place-items-center">
                      <AvatarSVG cfg={p.avatar_cfg || DEFAULT_AVATAR} size={36}/>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-[Fredoka] font-bold text-brand-text truncate">{p.name}</div>
                      {(p.game_name || p.level) && (
                        <div className="text-xs text-brand-muted truncate" data-testid={`vsh-peer-${p.user_id}-meta`}>
                          {p.game_name || "—"}
                          {p.level ? <> · <span className="text-violet-600 font-bold">{t("vsh_level_label")} {p.level}</span></> : null}
                        </div>
                      )}
                    </div>
                    <Button size="sm" disabled={pendingInviteTo === p.user_id} onClick={() => invitePeer(p)} data-testid={`vsh-invite-${p.user_id}`} className="bg-violet-500 hover:bg-violet-600 rounded-full font-bold">
                      {pendingInviteTo === p.user_id ? `${t("vsh_waiting_short")} ${pendingInviteTimer}s` : <>{t("vsh_challenge")} <ArrowRight className="w-3 h-3 ml-1"/></>}
                    </Button>
                  </li>
                ))}
              </ul>
            )
          )}
        </Step>

        {/* Private duel block */}
        <div className="mt-8 rounded-3xl bg-brand-bg2 shadow-md border-2 border-violet-100 p-5">
          <button onClick={() => setShowPrivate((s) => !s)} className="w-full text-left flex items-center justify-between" data-testid="vsh-private-toggle">
            <div className="flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-violet-600"/>
              <h2 className="text-xl font-[Fredoka] font-bold text-brand-text">{t("vsh_private_title")}</h2>
            </div>
            <span className="text-sm text-brand-muted">{showPrivate ? "▲" : "▼"}</span>
          </button>
          {showPrivate && (
            <div className="mt-4 grid sm:grid-cols-2 gap-4">
              <div>
                <div className="text-sm font-bold text-brand-text mb-2">{t("vsh_private_host_title")}</div>
                <Button onClick={createPrivate} className="w-full bg-violet-500 hover:bg-violet-600 rounded-full font-bold" data-testid="vsh-private-create">
                  {t("vsh_private_generate")}
                </Button>
                {privateCode && (
                  <div className="mt-3 flex items-center gap-2 bg-violet-50 rounded-xl p-3 border-2 border-violet-200">
                    <div className="font-mono text-3xl font-extrabold text-violet-700 tracking-widest flex-1" data-testid="vsh-private-code">{privateCode}</div>
                    <button onClick={() => { navigator.clipboard.writeText(privateCode); toast.success(t("vsh_copied")); }} className="p-2 rounded-lg hover:bg-violet-100"><Copy className="w-4 h-4 text-violet-700"/></button>
                  </div>
                )}
              </div>
              <div>
                <div className="text-sm font-bold text-brand-text mb-2">{t("vsh_private_enter_title")}</div>
                <div className="flex gap-2">
                  <Input value={enterCode} onChange={(e) => setEnterCode(e.target.value.replace(/[^\d]/g, "").slice(0,4))} placeholder="0000" inputMode="numeric" className="font-mono text-2xl tracking-widest text-center" data-testid="vsh-private-enter"/>
                  <Button onClick={joinPrivate} className="bg-green-500 hover:bg-green-600 rounded-full font-bold" data-testid="vsh-private-join">{t("vsh_private_enter_btn")}</Button>
                </div>
                <div className="text-xs text-brand-muted mt-2">{t("vsh_private_hint")}</div>
              </div>
            </div>
          )}
        </div>

        <div className="mt-6 text-center">
          <Link to="/" className="text-sm text-brand-muted hover:text-violet-700 underline" data-testid="vsh-back-home">{t("vsh_back_home")}</Link>
        </div>
      </section>

      {/* Incoming invite dialog */}
      <AnimatePresence>
        {/* Rematch invites are rendered by the global App-level listener
            (components/GlobalVsHumanRematch.jsx) so they show anywhere in
            the app, not just on this page. Only render the normal invite
            dialog here for standard duel invites. */}
        {invite && !invite.rematch && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-slate-900/70 grid place-items-center p-4" data-testid="vsh-invite-dialog">
            <motion.div initial={{ scale: 0.85 }} animate={{ scale: 1 }} className="bg-brand-bg2 rounded-3xl shadow-2xl w-full max-w-sm p-6 text-center">
              <div className="w-16 h-16 mx-auto mb-3 rounded-full overflow-hidden bg-violet-100 grid place-items-center">
                <AvatarSVG cfg={invite.from.avatar_cfg || DEFAULT_AVATAR} size={60}/>
              </div>
              <h3 className="text-xl font-[Fredoka] font-bold text-brand-text">{t("vsh_invite_title")}</h3>
              <p className="text-brand-muted mt-1" dangerouslySetInnerHTML={{ __html: t("vsh_invite_sub", { name: invite.from.name }) }} />
              <p className="text-xs text-brand-muted mt-2" data-testid="vsh-invite-countdown">
                {t("vsh_invite_countdown_prefix")} <b className={inviteTimer <= 3 ? "text-rose-600" : "text-violet-600"}>{inviteTimer}s</b>
              </p>
              <div className="flex gap-2 mt-4">
                <Button onClick={() => respondInvite(true)} className="flex-1 bg-green-500 hover:bg-green-600 rounded-full font-bold" data-testid="vsh-invite-accept">{t("vsh_accept")}</Button>
                <Button onClick={() => respondInvite(false)} variant="outline" className="flex-1 rounded-full font-bold" data-testid="vsh-invite-reject"><X className="w-4 h-4 mr-1"/>{t("vsh_reject")}</Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Avatar editor — saves to sessionStorage + (if logged-in) backend. */}
      {showAvatarPicker && (
        <AvatarBuilder
          current={avatarCfg}
          onSave={(cfg) => {
            try { sessionStorage.setItem("player_avatar_cfg", JSON.stringify(cfg)); } catch { /* ignore */ }
            try { localStorage.setItem("player_avatar_cfg", JSON.stringify(cfg)); } catch { /* ignore */ }
            setAvatarCfg(cfg);
            setShowAvatarPicker(false);
            // Re-announce ourselves so the new avatar propagates to the lobby
            // immediately. The existing `hello` useEffect handles this via the
            // avatarCfg dependency, but we also broadcast eagerly for snappier UX.
            if (connected && name.trim() && selectedGame) {
              send({
                type: "hello",
                name: name.trim().slice(0, 24),
                game_id: selectedGame.id,
                level: parseInt(selectedLevel, 10) || 1,
                tier_id: selectedTierId || null,
                lang,
                avatar_cfg: cfg,
              });
            }
          }}
          onClose={() => setShowAvatarPicker(false)}
        />
      )}
    </SiteChrome>
  );
}

function Step({ number, title, hint, children }) {
  return (
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-2">
        <span className="w-7 h-7 rounded-full bg-violet-500 text-white grid place-items-center font-bold text-sm">{number}</span>
        <h2 className="text-lg md:text-xl font-[Fredoka] font-bold text-brand-text">{title}</h2>
        {hint && <span className="text-xs text-brand-muted ml-1 italic">— {hint}</span>}
      </div>
      {children}
    </div>
  );
}
